import './journeyEdit.scss';

import { Button } from '@transferz/fe-component-library/src/components/button/Button';
import { ContentHeader } from '@transferz/fe-component-library/src/components/contentHeader/ContentHeader';
import CircleCheckIcon from '@transferz/fe-component-library/src/components/icons/CircleCheckIcon';
import Notification from '@transferz/fe-component-library/src/components/notification/Notification';
import { NotificationVariants } from '@transferz/fe-component-library/src/types/notification';
import { FormErrorCode, IError, JourneyChangeAutomationType } from '@transferz/fe-models';
import { GraphQLError } from 'graphql';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useLocalize } from 'localize-react';
import React, { useEffect, useState } from 'react';
import { useLocation } from 'wouter';

import { getErrors } from '../../apollo/utils';
import { Loading } from '../../components/loading/Loading';
import { useMutationConfirmJourneyChanges } from '../../queries/booking/hooks/useMutationConfirmJourneyChanges';
import { useMutationProcessJourneyChanges } from '../../queries/booking/hooks/useMutationProcessJourneyChanges';
import { useQueryJourney } from '../../queries/booking/hooks/useQueryJourney';
import { useQueryLocationByGeoPosition } from '../../queries/geo/hooks/useQueryLocationByGeoPosition';
import { formatDate, formatTime, futureDate } from '../../utils/utils';
import { BookingStatus } from '../journey/components/bookingStatus/BookingStatus';
import { AdditionalPayment } from './components/additionalPayment/AdditionalPayment';
import { PassengerDetailsEdit } from './components/passengerDetailsEdit/PassengerDetailsEdit';
import { TransferDetailsEdit } from './components/transferDetailsEdit/TransferDetailsEdit';
import { TripDetailsEdit } from './components/tripDetailsEdit/TripDetailsEdit';
import { FormContext } from './FormContext';

const JourneyEdit: React.FC<any> = ({ params }) => {
  const [formData, setFormData] = useState({} as any);
  const [formErrors, setFormErrors] = useState({} as any);
  const [journeyChangeData, setJourneyChangeData] = useState({} as any);
  const [processJourneyChanges] = useMutationProcessJourneyChanges();
  const [confirmJourneyChanges] = useMutationConfirmJourneyChanges();
  const { translate } = useLocalize();
  const [, setLocation] = useLocation();
  const flags = useFlags();
  const { data, loading } = useQueryJourney({
    variables: {
      id: Number(params.journeyId),
      bookingCode: params.bookingCode,
      includeCancellationDetails: true,
      includePickupTimeline: false,
    },
  });
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationVariant, setNotificationVariant] = useState(NotificationVariants.info);
  const [notificationVisible, setNotificationVisible] = useState(false);
  const formattedDate = formatDate(data?.journey?.journeyExecutionInfo?.pickupDate, 'YYYY-MM-DD');
  const formattedTime = formatTime(data?.journey?.journeyExecutionInfo?.pickupDate, 'HH:mm');
  const pickUpDate = data?.journey?.journeyExecutionInfo?.pickupDate;
  const pickUpTimeZone = data?.journey?.journeyExecutionInfo?.pickupTimeZone;
  const allowedToModifyJourney = data?.journey?.travellerInfo?.allowedToModifyJourney;
  const isJourneyEditable = (futureDate(pickUpDate, pickUpTimeZone, 'hours') >= 24
    && flags?.enableJourneyModification && allowedToModifyJourney
  );
  const { refetch: getLocationByGeoPosition } = useQueryLocationByGeoPosition({
    variables: {
      data: {
        address: '',
      },
      skip: true,
    },
  });

  useEffect(() => {
    const { travellerInfo, originLocation, destinationLocation } = data?.journey || {};
    setFormData({
      ...formData,
      travellerInfo,
      originLocation: originLocation?.address?.formattedAddress,
      destinationLocation: destinationLocation?.address?.formattedAddress,
      pickupDate: formattedDate,
      pickupTime: formattedTime,
    });
  }, [data]);

  const handleSubmit = async (event: any) => {
    event.preventDefault();
    let success: boolean = false;
    let shouldShowNotification = true;
    let processJourneyChangesData;
    const { pickupDate, pickupTime, travellerInfo, originLocation, destinationLocation } = formData;
    let locationId: number | null = null;
    const isOriginAddressChanged = originLocation && (originLocation !== data?.journey?.originLocation?.address?.formattedAddress);
    const isDestinationAddressChanged = destinationLocation && (destinationLocation !== data?.journey.destinationLocation.address.formattedAddress);
    const pickup = {
      pickupDate: `${pickupDate}T${pickupTime}:00`,
      pickupTimeZone: data?.journey?.journeyExecutionInfo?.pickupTimeZone,
    };
    if (isOriginAddressChanged || isDestinationAddressChanged) {
      try {
        const result = await getLocationByGeoPosition({
          data: { address: isOriginAddressChanged ? originLocation : isDestinationAddressChanged ? destinationLocation : '' },
          skip: false,
        });
        locationId = result?.data?.locationByGeoPosition?.id;
      } catch (e) {
        console.log('can not get location by geo position with error ', e);
      }
    }
    const isInvalid = Object.values(formErrors).includes(true);
    if (isInvalid) {
      return false;
    }
    try {
      if (isJourneyEditable) {
        const processJourneyChangesResponse = await processJourneyChanges({
          variables: {
            params: {
              pickup,
              journeyId: data?.journey?.id,
              travellerInfo: { ...travellerInfo, __typename: undefined },
              originLocation: isOriginAddressChanged && locationId
                ? { locationId }
                : null,
              destinationLocation: isDestinationAddressChanged && locationId
                ? { locationId }
                : null,
            },
          },
        });
        processJourneyChangesData = processJourneyChangesResponse?.data?.processJourneyChanges;
        if (!processJourneyChangesData?.id) {
          setNotificationMessage(translate('booking_noChangesToSave'));
          setNotificationVariant(NotificationVariants.info);
        } else if (processJourneyChangesData?.isPaymentRequired) {
          if (flags?.showPaymentPage) {
            setJourneyChangeData(processJourneyChangesData);
            setIsPopupOpen(true);
            shouldShowNotification = false;
          } else {
            setNotificationMessage(translate('booking_changeNotSupported'));
            setNotificationVariant(NotificationVariants.neutral);
          }
        } else if (processJourneyChangesData?.changeAutomationType === JourneyChangeAutomationType.AutomationNotSupported
          || processJourneyChangesData?.changeAutomationType === JourneyChangeAutomationType.PaymentAndReOrchestration) {
          setNotificationMessage(translate('booking_closeToPickupTime'));
        } else {
          if (!journeyChangeData?.id) {
            await confirmJourneyChanges({
              variables: {
                id: processJourneyChangesData?.id,
              },
            });
            success = true;
          }
          setFormErrors({});
        }
      } else {
        setNotificationMessage(translate('booking_outOfEditingTime'));
        setNotificationVariant(NotificationVariants.neutral);
      }
    } catch (error) {
      (error as any)?.graphQLErrors.forEach((graphQLError: GraphQLError) => {
        const errors = getErrors(graphQLError);
        errors?.forEach(({ code }: IError) => {
          if (code === FormErrorCode.InvalidJourneyStatus) {
            setNotificationMessage(translate(`errors_${code}`));
          } else if (code === FormErrorCode.InvalidPickupChange) {
            setNotificationMessage(translate('n_changeCannotBeMade'));
          } else {
            setNotificationMessage(translate('booking_somethingWentWrong'));
          }
        });
      });
      setNotificationVariant(NotificationVariants.error);
    } finally {
      if (success) {
        setLocation(`/booking/${params.bookingCode}/journey/${params.journeyId}?journeyChangeSuccess=true`);
      } else {
        if (!processJourneyChangesData?.isPaymentRequired) {
          window.scrollTo({
            top: 0,
            behavior: 'smooth',
          });
        }
        setNotificationVisible(shouldShowNotification);
      }
    }
  };

  return (
    <div className="journey white-background">
      {notificationVisible && <Notification
        variant={notificationVariant}
        message={notificationMessage}
        isSnackBar={true}
        closeIcon={true}
        onClose={() => [setNotificationVisible(false), setJourneyChangeData({})]}
        className={`journey-notification ${journeyChangeData?.isPaymentRequired ? 'journey-notification-position display-h' : ''}`}
      />
      }
      <ContentHeader
        bookingLink={`${window.tz_globalConfigs.customUrlPrefix || ''}/mybooking/booking/${params.bookingCode}`}
        bookingLinkText={translate('viewBooking')}
        title={translate('yourJourney')}
        bookingCode={data?.journey?.journeyCode}
      >
        <BookingStatus
          journey={data?.journey}
          classes="mb-24"
        />
        <div className="d-flex actions-container">
          <Button
            type="button"
            className="edit-button"
            priority="secondary"
            label={translate('back')}
            onClick={() => setLocation(`/booking/${params.bookingCode}/journey/${params.journeyId}`)}
            data-button-id="cancel-modify-journey"
          />
          <Button
            type="submit"
            className="edit-button"
            priority="primary"
            label={translate('booking_confirmChanges')}
            formId="editTripDetails"
            icon={<CircleCheckIcon />}
            data-button-id="confirm-modify-journey"
            data-testid="confirm_modify-journey--desktop"
          />
        </div>
      </ContentHeader>
      <div className="journey-content journey-details-edit width-w">
        {loading && <Loading />}
        {!loading && (
          <form className="form journey-details" id="editTripDetails" onSubmit={handleSubmit}>
            <FormContext.Provider value={{
              formData,
              setFormData,
              formErrors,
              setFormErrors,
            }}>
              <div className="journey-left-side">
                <TripDetailsEdit
                  journey={data?.journey}
                />
                <PassengerDetailsEdit
                  travellerInfo={{ ...data?.journey?.travellerInfo }}
                />
              </div>
              <div className="journey-right-side">
                <TransferDetailsEdit
                  journey={data?.journey}
                />
              </div>
            </FormContext.Provider>
            <Button
              type="submit"
              className="edit-button"
              priority="primary"
              label={translate('booking_confirmChanges')}
              formId="editTripDetails"
              icon={<CircleCheckIcon />}
              data-button-id="confirm-modify-journey"
              data-testid="confirm_modify-journey--mobile"
            />
          </form>
        )}
      </div>
      {isPopupOpen && (<AdditionalPayment
        params={params}
        isPopupOpen={isPopupOpen}
        setIsPopupOpen={setIsPopupOpen}
        currencyCode={data?.journey?.priceCalculationInfo?.currencyCode}
        journeyChangeData={journeyChangeData}
      />
      )}
    </div>
  );
};

export default JourneyEdit;
